import React from 'react'
import dayjs from 'dayjs'
import { Grid, GridCell } from '@mobi/component-library/Legacy/Grid'
import { keys } from '@classic/Foundation/Analytics/AnalyticsDataLayer'
import { trackBonusBetEvent } from '@classic/Foundation/Analytics/GoogleTagManagerService'
import { useObservableImmutable } from '@core/Utils/hooks'
import { state$ } from '@core/State/UserAccount/userAccountDriver'
import {
  AmountLabelStyled,
  BetsRemainingLabelStyled,
  BonusBetActionButtonsStyled,
  BonusBetLabelStyled,
  BonusBetListItemButtonStyled,
  ExpiryLabelStyled,
  MoneyStyled,
} from './BonusBetListItem.styles'
import { Button } from '@core/Components/Buttons/Button'

export const BonusBetListItem: React.FC<{
  id: number | string
  name: string
  expiry: Date
  amount: number
  selected?: boolean
  disabled?: boolean
  isBonusCash?: boolean
  handleClick: () => void
  betCount?: number
  canBeSplit?: boolean
  openSplitBonusBetTray?: () => void
}> = ({
  id,
  name,
  expiry,
  amount,
  selected,
  disabled,
  isBonusCash = false,
  handleClick,
  betCount,
  canBeSplit,
  openSplitBonusBetTray,
}) => {
  const {
    accountNumber = null,
    accountBalance = null,
    bonusBetBalance = null,
    bonusCashBalance = null,
  } = useObservableImmutable(state$, [
    'isLoggedIn',
    'accountBalance',
    'accountNumber',
    'bonusBetBalance',
    'bonusCashBalance',
  ])

  const trackClick = () => {
    const trackerKey = getTrackingKey(selected, isBonusCash)
    trackBonusBetEvent(trackerKey, {
      name,
      accountNumber: accountNumber ?? '',
      accountBalance,
      bonusBetBalance,
      bonusCashBalance,
    })
  }

  const hasMultipleTokens = betCount && betCount > 1

  return (
    <BonusBetListItemButtonStyled
      data-testid={`bonus-bet-item-${id}`}
      className={hasMultipleTokens ? 'split-tokens' : ''}
      selected={selected ?? false}
      disabled={disabled}
      onClick={
        !canBeSplit
          ? () => {
              handleClick()
              trackClick()
            }
          : undefined
      }
      isBonusCash={isBonusCash}
    >
      <Grid margin='0' padding='0'>
        <BonusBetLabelStyled isBonusCash={isBonusCash} selected={selected}>
          {name}
        </BonusBetLabelStyled>
        <ExpiryLabelStyled isBonusCash={isBonusCash} selected={selected}>
          Expires: {dayjs(expiry).format('DD/MM/YY')}
        </ExpiryLabelStyled>
        <GridCell align='center'>
          {hasMultipleTokens ? (
            <BetsRemainingLabelStyled>{betCount} Remaining</BetsRemainingLabelStyled>
          ) : null}
        </GridCell>
        <AmountLabelStyled isBonusCash={isBonusCash} selected={selected}>
          <MoneyStyled amount={amount} />
        </AmountLabelStyled>
      </Grid>
      {canBeSplit ? (
        <BonusBetActionButtonsStyled>
          {selected ? (
            <Button color='secondary' onClick={() => handleClick()}>
              Cancel
            </Button>
          ) : (
            <>
              <Button color='secondary' onClick={openSplitBonusBetTray}>
                Split Bonus Bet
              </Button>
              <Button
                color='primary'
                onClick={() => {
                  handleClick()
                  trackClick()
                }}
              >
                Apply Bonus Bet
              </Button>
            </>
          )}
        </BonusBetActionButtonsStyled>
      ) : null}
    </BonusBetListItemButtonStyled>
  )
}

// Local Helpers

function getTrackingKey(selected?: boolean, isBonusCash?: boolean) {
  return isBonusCash
    ? selected
      ? keys.quickbetBonusCashUnselected
      : keys.quickbetBonusCashSelected
    : selected
      ? keys.quickbetBonusBetUnselected
      : keys.quickbetBonusBetSelected
}
